/*
 * BioIndex
 * Copyright (c) 2026 BioIndex™. All rights reserved.
 * Unauthorized copying, scraping, redistribution, or commercial reuse is prohibited.
 */

import { useEffect } from "react";
import { auth } from "../firebase";
import defaultProfile from "../assets/default.png";

function isBioIndexProfilePhoto(url) {
  if (!url) return false;

  const value = String(url);

  return (
    value.includes("firebasestorage.googleapis.com") &&
    (value.includes("/o/profile%2F") || value.includes("/o/profile/"))
  );
}

export default function ProfileSocial({
  friends,
  profiles,
  requestsReceived,
  requestsSent,
  friendsTaxa,
  friendsTaxaLoading,
  friendsModalOpen,
  setFriendsModalOpen,
  requestsModalOpen,
  setRequestsModalOpen,
  discoverModalOpen,
  setDiscoverModalOpen,
  acceptRequest,
  declineRequest,
  navigate,
  addFriendRequest,
  SectionRefreshOverlay,
  sectionLoading,
  sectionProgress,
  showPanel = true,
  currentProfileUID = null,
  currentProfileName = "",
  isOwnProfile = true,
  isAdmin = false,
}) {
  useEffect(() => {
    if (!friendsModalOpen && !requestsModalOpen && !discoverModalOpen) return;
    const previousoverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = previousoverflow;
    };
  }, [friendsModalOpen, requestsModalOpen, discoverModalOpen]);

  function openAdminProfile(targetUid, closeModal) {
    if (!isAdmin || !targetUid || targetUid === auth.currentUser?.uid) {
      return;
    }

    closeModal?.();
    navigate(`/profile/${targetUid}?adminMode=true`);
  }

  return (
    <>
      {showPanel && (
        <section
          className="bioindex-social-card relative rounded-2xl border border-[#93CA87]/40 bg-white p-4 shadow-sm"
          aria-label="Social"
        >
          <SectionRefreshOverlay
            show={sectionLoading.social}
            progress={sectionProgress.social}
            label="Refreshing social"
          />
          <div className="bioindex-social-heading-row">
            <h2
              className="text-2xl text-[#374049]"
              style={{ fontFamily: "Cormorant Garamond" }}
            >
              Social
            </h2>
          </div>
          <div className="bioindex-social-actions">
            <button
              type="button"
              onClick={() => setFriendsModalOpen(true)}
              title="Friends"
              aria-label={`Friends (${friends.length})`}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="9" cy="8" r="3" />
                <path d="M3 20v-2a6 6 0 0 1 12 0v2M17 7a3 3 0 0 1 0 5.5M17 15a5 5 0 0 1 4 5" />
              </svg>
              <span>Friends</span>
              <strong>{friends.length}</strong>
            </button>
            <button
              type="button"
              onClick={() => setRequestsModalOpen(true)}
              title="Friend requests"
              aria-label={`Friend requests (${requestsReceived.length})`}
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="9" cy="8" r="3" />
                <path d="M3 20v-2a6 6 0 0 1 12 0v2M18 8v7m-3.5-3.5h7" />
              </svg>
              <span>Requests</span>
              {requestsReceived.length > 0 && (
                <strong className="bioindex-social-pending">
                  {requestsReceived.length}
                </strong>
              )}
            </button>
            <button
              type="button"
              onClick={() => setDiscoverModalOpen(true)}
              title="Discover people"
              aria-label="Discover people"
            >
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <circle cx="10.5" cy="10.5" r="6.5" />
                <path d="m15.5 15.5 5 5M13.5 7.5l-2 4-4 2 2-4z" />
              </svg>
              <span>Discover</span>
            </button>
          </div>
        </section>
      )}

      {friendsModalOpen && (
        <div
          className="fixed inset-0 bg-[#374049] z-50 overflow-hidden px-3 sm:px-8 py-4"
          onClick={() => setFriendsModalOpen(false)}
        >
          <div
            className="mx-auto h-[calc(100dvh-2rem)] w-full max-w-4xl overflow-y-auto overscroll-contain rounded-3xl bg-white px-5 pt-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] sm:h-[calc(100vh-2rem)] sm:p-8 shadow-xl border border-[#93CA87]/40"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3
                className="text-2xl text-[#374049]"
                style={{ fontFamily: "Cormorant Garamond" }}
              >
                Friends
              </h3>

              <div className="flex flex-wrap gap-2 justify-end">
                <button
                  onClick={() => setFriendsModalOpen(false)}
                  className="rounded-xl border border-[#01767A] bg-white px-4 py-2 text-sm font-semibold text-[#374049] hover:bg-[#01767A]/10 transition"
                >
                  ← Return to{" "}
                  {!isOwnProfile && currentProfileName
                    ? `${currentProfileName.split(" ")[0]}'s Profile`
                    : "Profile"}
                </button>

                {!isOwnProfile && (
                  <button
                    onClick={() => {
                      setFriendsModalOpen(false);
                      navigate("/profile");
                    }}
                    className="rounded-xl bg-[#01767A] px-4 py-2 text-sm font-semibold text-white hover:bg-[#24C3BE] transition"
                  >
                    Return to Your Profile
                  </button>
                )}
              </div>
            </div>

            {friendsTaxaLoading ? (
              <div className="relative min-h-64 bg-white p-10">
                <SectionRefreshOverlay
                  show={true}
                  progress={sectionProgress.social}
                  label="Loading friends"
                />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {profiles
                  .filter((p) => friends.includes(p.uid))
                  .sort(
                    (a, b) =>
                      (friendsTaxa[b.uid]?.speciesCount ?? 0) -
                      (friendsTaxa[a.uid]?.speciesCount ?? 0),
                  )
                  .map((user) => {
                    const isCurrentProfile = user.uid === currentProfileUID;

                    return (
                      <div
                        key={user.uid}
                        onClick={() => {
                          if (isCurrentProfile) return;
                          setFriendsModalOpen(false);
                          navigate(`/profile/${user.uid}`);
                        }}
                        className={`relative rounded-2xl border transition flex items-center gap-3 p-3 sm:gap-5 sm:p-5 text-left overflow-hidden before:absolute before:left-0 before:top-0 before:h-full before:w-1 ${
                          isCurrentProfile
                            ? "cursor-default border-slate-200 bg-slate-100 opacity-55 before:bg-slate-300"
                            : "cursor-pointer border-[#374049]/15 bg-[#fdfbf8] shadow-[0_8px_22px_rgba(55,64,73,0.08)] hover:shadow-[0_12px_28px_rgba(55,64,73,0.14)] hover:-translate-y-[1px] before:bg-[#01767A]"
                        }`}
                      >
                        <div className="w-16 h-16 sm:w-20 sm:h-20 shrink-0 rounded-2xl overflow-hidden border border-[#93CA87]/40 shadow-sm bg-white">
                          <img
                            src={
                              isBioIndexProfilePhoto(user.photoURL)
                                ? user.photoURL
                                : defaultProfile
                            }
                            alt=""
                            className="w-full h-full object-cover"
                          />
                        </div>

                        <div
                          className={`min-w-0 flex-1 ${
                            isAdmin &&
                            user.uid !== auth.currentUser?.uid &&
                            !isCurrentProfile
                              ? "pr-10 sm:pr-0"
                              : ""
                          }`}
                        >
                          <div className="break-words text-sm font-semibold leading-tight text-[#374049] sm:text-base">
                            {user.name}
                          </div>

                          <div className="mt-0.5 truncate text-[11px] text-[#374049]/60 sm:text-xs">
                            {user.title || "Beginner"}
                          </div>

                          <div className="mt-2 flex flex-wrap items-center gap-1.5 sm:gap-2">
                            <div className="inline-flex h-7 items-center gap-1.5 rounded-full bg-[#01767A] px-3 text-xs font-semibold text-white shadow-sm">
                              <svg
                                viewBox="0 0 24 24"
                                className="h-3.5 w-3.5 text-[#FCFFF6]"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M12 21V10" />
                                <path d="M12 14C7 14 4 11 4 6c5 0 8 3 8 8z" />
                                <path d="M12 12c0-5 3-8 8-8 0 5-3 8-8 8z" />
                              </svg>

                              {(
                                friendsTaxa[user.uid]?.speciesCount ?? 0
                              ).toLocaleString()}

                              <span>species</span>
                            </div>

                            {isAdmin &&
                              user.uid !== auth.currentUser?.uid &&
                              !isCurrentProfile && (
                                <button
                                  type="button"
                                  onClick={(event) => {
                                    event.stopPropagation();

                                    openAdminProfile(user.uid, () =>
                                      setFriendsModalOpen(false),
                                    );
                                  }}
                                  className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#374049] bg-[#374049] text-white shadow-sm transition hover:border-[#01767A] hover:bg-[#01767A] sm:static sm:h-7 sm:w-auto sm:gap-1.5 sm:px-3 sm:text-xs sm:font-semibold"
                                  aria-label="Manage Profile"
                                  title="Manage Profile"
                                >
                                  <svg
                                    viewBox="0 0 24 24"
                                    className="h-3.5 w-3.5"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    aria-hidden="true"
                                  >
                                    <path d="M12 3 19 6v5c0 4.8-2.8 8.2-7 10-4.2-1.8-7-5.2-7-10V6l7-3Z" />
                                  </svg>

                                  <span className="hidden sm:inline">
                                    Manage Profile
                                  </span>
                                </button>
                              )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
      )}

      {requestsModalOpen && (
        <div
          className="fixed inset-0 bg-[#374049] z-50 overflow-hidden px-3 sm:px-8 py-4"
          onClick={() => setRequestsModalOpen(false)}
        >
          <div
            className="mx-auto h-[calc(100dvh-2rem)] w-full max-w-4xl overflow-y-auto overscroll-contain rounded-3xl bg-white px-5 pt-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] sm:h-[calc(100vh-2rem)] sm:p-8 shadow-xl border border-[#93CA87]/40"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3
                className="text-2xl text-[#374049]"
                style={{ fontFamily: "Cormorant Garamond" }}
              >
                Friend Requests
              </h3>

              <button
                onClick={() => setRequestsModalOpen(false)}
                className="rounded-xl border border-[#01767A] bg-white px-4 py-2 text-sm font-semibold text-[#374049] hover:bg-[#01767A]/10 transition"
              >
                ← Return to Profile
              </button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {requestsReceived.length === 0 ? (
                <div className="text-center text-slate-500 py-10">
                  Currently there are no friend requests
                </div>
              ) : (
                requestsReceived.map((r) => (
                  <div
                    key={r.requestId}
                    className="relative rounded-2xl border border-[#374049]/15 bg-[#fdfbf8] shadow-[0_8px_22px_rgba(55,64,73,0.08)] hover:shadow-[0_12px_28px_rgba(55,64,73,0.14)] hover:-translate-y-[1px] transition flex items-center gap-3 p-3 text-left overflow-hidden before:absolute before:left-0 before:top-0 before:h-full before:w-1 before:bg-[#01767A] sm:gap-5 sm:p-5"
                  >
                    <div className="w-16 h-16 sm:w-20 sm:h-20 shrink-0 rounded-2xl overflow-hidden border border-[#93CA87]/40 shadow-sm bg-white">
                      <img
                        src={
                          isBioIndexProfilePhoto(r.photoURL)
                            ? r.photoURL
                            : defaultProfile
                        }
                        alt={r.name}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    <div
                      className={`min-w-0 flex-1 ${
                        isAdmin && r.uid !== auth.currentUser?.uid
                          ? "pr-10 sm:pr-0"
                          : ""
                      }`}
                    >
                      <div className="break-words text-sm font-semibold leading-tight text-[#374049] sm:text-base">
                        {r.name}
                      </div>

                      <div className="mt-0.5 truncate text-[11px] italic text-[#374049]/60 sm:text-xs">
                        {r.title || "Beginner"}
                      </div>

                      <div className="mt-2 flex flex-wrap items-center gap-1.5 sm:gap-2">
                        <div className="inline-flex h-7 items-center gap-1.5 rounded-full bg-[#01767A] px-3 text-xs font-semibold text-white shadow-sm">
                          <svg
                            viewBox="0 0 24 24"
                            className="h-3.5 w-3.5 text-[#FCFFF6]"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d="M12 21V10" />
                            <path d="M12 14C7 14 4 11 4 6c5 0 8 3 8 8z" />
                            <path d="M12 12c0-5 3-8 8-8 0 5-3 8-8 8z" />
                          </svg>

                          {(
                            r.speciesCount ??
                            friendsTaxa[r.uid]?.speciesCount ??
                            0
                          ).toLocaleString()}

                          <span>species</span>
                        </div>

                        <button
                          onClick={() => acceptRequest(r.requestId, r.uid)}
                          className="inline-flex h-7 items-center rounded-full bg-[#01767A] px-3 text-xs font-semibold text-white shadow-sm hover:bg-[#24C3BE] transition"
                        >
                          Accept
                        </button>

                        <button
                          onClick={() => declineRequest(r.requestId)}
                          className="inline-flex h-7 items-center rounded-full border border-[#01767A] bg-white px-3 text-xs font-semibold text-[#374049] hover:bg-[#01767A]/10 transition"
                        >
                          Deny
                        </button>

                        {isAdmin && r.uid !== auth.currentUser?.uid && (
                          <button
                            type="button"
                            onClick={() =>
                              openAdminProfile(r.uid, () =>
                                setRequestsModalOpen(false),
                              )
                            }
                            className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#374049] bg-[#374049] text-white shadow-sm transition hover:border-[#01767A] hover:bg-[#01767A] sm:static sm:h-7 sm:w-auto sm:gap-1.5 sm:px-3 sm:text-[11px] sm:font-semibold"
                            aria-label="Manage Profile"
                            title="Manage Profile"
                          >
                            <svg
                              viewBox="0 0 24 24"
                              className="h-3.5 w-3.5"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              aria-hidden="true"
                            >
                              <path d="M12 3 19 6v5c0 4.8-2.8 8.2-7 10-4.2-1.8-7-5.2-7-10V6l7-3Z" />
                            </svg>

                            <span className="hidden sm:inline">
                              Manage Profile
                            </span>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {discoverModalOpen && (
        <div
          className="fixed inset-0 bg-[#374049] z-50 overflow-hidden px-3 sm:px-8 py-4"
          onClick={() => setDiscoverModalOpen(false)}
        >
          <div
            className="mx-auto h-[calc(100dvh-2rem)] w-full max-w-4xl overflow-y-auto overscroll-contain rounded-3xl bg-white px-5 pt-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] sm:h-[calc(100vh-2rem)] sm:p-8 shadow-xl border border-[#93CA87]/40"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h3
                className="text-2xl text-[#374049]"
                style={{ fontFamily: "Cormorant Garamond" }}
              >
                Discover People
              </h3>

              <button
                onClick={() => setDiscoverModalOpen(false)}
                className="rounded-xl border border-[#01767A] bg-white px-4 py-2 text-sm font-semibold text-[#374049] hover:bg-[#01767A]/10 transition"
              >
                ← Return to Profile
              </button>
            </div>

            {profiles.filter(
              (p) => p.uid !== auth.currentUser.uid && !friends.includes(p.uid),
            ).length === 0 ? (
              <div className="text-center text-slate-500 py-10">
                No new people to discover right now
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {profiles
                  .filter(
                    (p) =>
                      p.uid !== auth.currentUser.uid &&
                      !friends.includes(p.uid),
                  )
                  .sort((a, b) => {
                    const bCount =
                      friendsTaxa[b.uid]?.speciesCount ?? b.speciesCount ?? 0;
                    const aCount =
                      friendsTaxa[a.uid]?.speciesCount ?? a.speciesCount ?? 0;

                    return bCount - aCount;
                  })
                  .map((user) => {
                    const incoming = requestsReceived.some(
                      (r) => r.uid === user.uid,
                    );
                    const sent = requestsSent.includes(user.uid);
                    const speciesCount =
                      friendsTaxa[user.uid]?.speciesCount ??
                      user.speciesCount ??
                      0;

                    return (
                      <div
                        key={user.uid}
                        className="relative rounded-2xl border border-[#374049]/15 bg-[#fdfbf8] shadow-[0_8px_22px_rgba(55,64,73,0.08)] hover:shadow-[0_12px_28px_rgba(55,64,73,0.14)] hover:-translate-y-[1px] transition flex items-center gap-3 p-3 text-left overflow-hidden before:absolute before:left-0 before:top-0 before:h-full before:w-1 before:bg-[#01767A] sm:gap-5 sm:p-5"
                      >
                        <div className="w-16 h-16 sm:w-20 sm:h-20 shrink-0 rounded-2xl overflow-hidden border border-[#93CA87]/40 shadow-sm bg-white">
                          <img
                            src={
                              isBioIndexProfilePhoto(user.photoURL)
                                ? user.photoURL
                                : defaultProfile
                            }
                            alt={user.name || "User profile"}
                            className="w-full h-full object-cover"
                          />
                        </div>

                        <div
                          className={`min-w-0 flex-1 ${
                            isAdmin && user.uid !== auth.currentUser?.uid
                              ? "pr-10 sm:pr-0"
                              : ""
                          }`}
                        >
                          <div className="break-words text-sm font-semibold leading-tight text-[#374049] sm:text-base">
                            {user.name}
                          </div>

                          <div className="mt-0.5 truncate text-[11px] italic text-[#374049]/60 sm:text-xs">
                            {user.title || "Beginner"}
                          </div>

                          <div className="mt-2 flex flex-wrap items-center gap-1.5 sm:gap-2">
                            <div className="inline-flex h-7 items-center gap-1.5 rounded-full bg-[#01767A] px-3 text-xs font-semibold text-white shadow-sm">
                              <svg
                                viewBox="0 0 24 24"
                                className="h-3.5 w-3.5 text-[#FCFFF6]"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2.2"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path d="M12 21V10" />
                                <path d="M12 14C7 14 4 11 4 6c5 0 8 3 8 8z" />
                                <path d="M12 12c0-5 3-8 8-8 0 5-3 8-8 8z" />
                              </svg>

                              {speciesCount.toLocaleString()}

                              <span>species</span>
                            </div>

                            {incoming ? (
                              <>
                                <button
                                  onClick={() => {
                                    const req = requestsReceived.find(
                                      (r) => r.uid === user.uid,
                                    );

                                    if (!req) return;

                                    acceptRequest(req.requestId, user.uid);
                                  }}
                                  className="inline-flex h-7 items-center rounded-full bg-[#01767A] px-3 text-xs font-semibold text-white shadow-sm hover:bg-[#24C3BE] transition"
                                >
                                  Accept
                                </button>

                                <button
                                  onClick={() => {
                                    const req = requestsReceived.find(
                                      (r) => r.uid === user.uid,
                                    );

                                    if (!req) return;

                                    declineRequest(req.requestId);
                                  }}
                                  className="inline-flex h-7 items-center rounded-full border border-[#01767A] bg-white px-3 text-xs font-semibold text-[#374049] hover:bg-[#01767A]/10 transition"
                                >
                                  Decline
                                </button>
                              </>
                            ) : sent ? (
                              <div className="inline-flex h-7 w-fit items-center rounded-full border border-[#374049]/15 bg-[#374049]/5 px-3 text-xs font-semibold text-[#374049]/55">
                                Request Sent
                              </div>
                            ) : (
                              <button
                                onClick={() => addFriendRequest(user.uid)}
                                className="inline-flex h-7 items-center rounded-full border border-[#01767A] bg-white px-3 text-xs font-semibold text-[#01767A] hover:bg-[#01767A]/10 transition"
                              >
                                Add Friend
                              </button>
                            )}

                            {isAdmin && user.uid !== auth.currentUser?.uid && (
                              <button
                                type="button"
                                onClick={() =>
                                  openAdminProfile(user.uid, () =>
                                    setDiscoverModalOpen(false),
                                  )
                                }
                                className="absolute right-3 top-3 inline-flex h-8 w-8 items-center justify-center rounded-full border border-[#374049] bg-[#374049] text-white shadow-sm transition hover:border-[#01767A] hover:bg-[#01767A] sm:static sm:h-7 sm:w-auto sm:gap-1.5 sm:px-3 sm:text-[11px] sm:font-semibold"
                                aria-label="Manage Profile"
                                title="Manage Profile"
                              >
                                <svg
                                  viewBox="0 0 24 24"
                                  className="h-3.5 w-3.5"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  aria-hidden="true"
                                >
                                  <path d="M12 3 19 6v5c0 4.8-2.8 8.2-7 10-4.2-1.8-7-5.2-7-10V6l7-3Z" />
                                </svg>

                                <span className="hidden sm:inline">
                                  Manage Profile
                                </span>
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
